import { useState } from 'react';
import { Alert, Button, Grid, NumberInput, Select, Table, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react';
import { ApiRequestError } from '../../../api/client';
import { parseNum } from '../../../lib/parseNum';
import { useActiveProducts, useAddRunConsumable, useAllProducts, useDeleteRunConsumable, useLocations } from '../api';
import { formatQty } from '../format';
import { pendingRecipeConsumables } from '../recipePreview';
import type { RecipeDto, RunConsumableLineDto, RunOutputLineDto } from '../types';

/** Consumables step of "Record a shift" (BUILD_SPEC.md §8.3: "consumables
 * (backflushed from recipe, editable)"). Shows the recipe's still-pending
 * requirements (auto-backflushed at close if never entered here) alongside any
 * ACTUAL consumable lines the operator has already recorded manually. */
export function RunConsumablesEditor({
  runId,
  siteId,
  lines,
  outputs,
  recipe,
  editable,
}: {
  runId: string;
  siteId: string;
  lines: RunConsumableLineDto[];
  outputs: RunOutputLineDto[];
  recipe: RecipeDto | undefined;
  editable: boolean;
}) {
  const locationsQuery = useLocations(siteId);
  // Picker offers ACTIVE products only; the lookup map covers ALL products
  // (matching the server's un-filtered products join — see api.ts's
  // useAllProducts doc) so an output line for a since-deactivated product still
  // counts toward the backflush tonnage, and pending/recorded rows still show
  // their product names.
  const productsQuery = useActiveProducts();
  const allProductsQuery = useAllProducts();
  const addConsumable = useAddRunConsumable(runId);
  const deleteConsumable = useDeleteRunConsumable(runId);

  const [productId, setProductId] = useState<string | null>(null);
  const [fromLocationId, setFromLocationId] = useState<string | null>(null);
  const [qty, setQty] = useState<number | string>('');

  const locations = locationsQuery.data?.items ?? [];
  const products = productsQuery.data?.items ?? [];
  const locationsById = new Map(locations.map((l) => [l.id, l]));
  const productsById = new Map((allProductsQuery.data?.items ?? []).map((p) => [p.id, p]));

  // baseUom lookup passed only once the ALL-products lookup has loaded, so a
  // still-loading product list can't be mistaken for "this run has no KG output
  // mass".
  const productsLoaded = Boolean(allProductsQuery.data);
  const pending = pendingRecipeConsumables(recipe, outputs, lines, productsLoaded ? productsById : undefined);
  // Null estimates with products loaded = zero KG-denominated output tonnage
  // (R6): the server will NOT backflush these — its `/close` rejects with
  // "Cannot backflush recipe consumable(s) …: this run recorded zero
  // KG-denominated output mass … Record them as ACTUAL quantities on the run
  // instead" (services/api/src/modules/production/runs.ts) — so the preview
  // must direct the operator to ACTUAL entry, not show 0-derived noise.
  const backflushImpossible = productsLoaded && pending.some((p) => p.estimatedQty === null);
  const parsedQty = parseNum(qty);

  function reset() {
    setProductId(null);
    setFromLocationId(null);
    setQty('');
    addConsumable.reset();
  }

  function handleAdd() {
    if (!productId || !fromLocationId || parsedQty === null) return;
    addConsumable.mutate({ productId, fromLocationId, qty: parsedQty.toFixed(3), source: 'ACTUAL' }, { onSuccess: reset });
  }

  return (
    <>
      <Title order={4} mb="xs">
        Consumables
      </Title>
      {pending.length > 0 && (
        <Alert
          color="graphite"
          variant="light"
          mb="sm"
          title={
            backflushImpossible
              ? 'From the recipe (must be recorded as actual quantities below)'
              : 'From the recipe (auto-backflushed at close if not entered here)'
          }
        >
          <Table.ScrollContainer minWidth={640}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Product</Table.Th>
                  <Table.Th>Per output tonne</Table.Th>
                  <Table.Th>Estimated qty</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pending.map((p) => (
                  <Table.Tr key={p.productId}>
                    <Table.Td>{productsById.get(p.productId)?.name ?? p.productId}</Table.Td>
                    <Table.Td>{p.qtyPerOutputTonne}</Table.Td>
                    {/* formatQty renders a null estimate as '—' */}
                    <Table.Td>{formatQty(p.estimatedQty)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {backflushImpossible && (
            <Text size="xs" mt="xs">
              These cannot be backflushed automatically: this run recorded zero KG-denominated output mass, so there is no
              output tonnage to compute the per-tonne quantities against. Record the actual quantities used below — the run
              cannot be closed otherwise.
            </Text>
          )}
        </Alert>
      )}
      {editable && (
        <Grid align="flex-end" mb="sm">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Select
              label="Product"
              placeholder="Product"
              data={products.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }))}
              value={productId}
              onChange={setProductId}
              searchable
              disabled={productsQuery.isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Select
              label="From location"
              placeholder="Location"
              data={locations.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
              value={fromLocationId}
              onChange={setFromLocationId}
              searchable
              disabled={locationsQuery.isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 8, sm: 2 }}>
            <NumberInput
              label="Qty"
              decimalScale={3}
              min={0}
              value={qty}
              onChange={setQty}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 4, sm: 2 }}>
            <Button
              mt={24}
              size="xs"
              variant="light"
              fullWidth
              disabled={!productId || !fromLocationId || parsedQty === null}
              loading={addConsumable.isPending}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Grid.Col>
        </Grid>
      )}
      {addConsumable.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} mb="sm">
          {addConsumable.error instanceof ApiRequestError ? addConsumable.error.message : 'Failed to add consumable'}
        </Alert>
      )}
      <Table.ScrollContainer minWidth={520}>
        <Table verticalSpacing="xs" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              <Table.Th>From</Table.Th>
              <Table.Th>Qty</Table.Th>
              <Table.Th>Source</Table.Th>
              {editable && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lines.map((line) => (
              <Table.Tr key={line.id}>
                <Table.Td>{productsById.get(line.productId)?.name ?? line.productId}</Table.Td>
                <Table.Td>{locationsById.get(line.fromLocationId)?.name ?? line.fromLocationId}</Table.Td>
                <Table.Td>{formatQty(line.qty)}</Table.Td>
                <Table.Td>{line.source}</Table.Td>
                {editable && (
                  <Table.Td>
                    <Button variant="subtle" color="red" size="compact-sm" onClick={() => deleteConsumable.mutate(line.id)}>
                      <IconTrash size={14} />
                    </Button>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {lines.length === 0 && (
          <Text c="dimmed" size="sm" mt="xs">
            {backflushImpossible
              ? "No consumables recorded yet — enter the recipe's requirements above as actual quantities (they cannot be backflushed automatically for this run)."
              : "No consumables recorded yet — the recipe's requirements above will be backflushed automatically at close."}
          </Text>
        )}
      </Table.ScrollContainer>
    </>
  );
}
